import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";
import Modal, { Field, Input, Select, GoldButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import { PhoneInput } from "@/components/PhoneInput";

const ROLES = [
    { value: "managing_partner", label: "Managing Partner" },
    { value: "senior_advocate", label: "Senior Advocate" },
    { value: "junior_advocate", label: "Junior Advocate" },
    { value: "associate_advocate", label: "Associate Advocate" },
    { value: "clerk", label: "Clerk" },
    { value: "receptionist", label: "Receptionist" },
    { value: "accountant", label: "Accountant" },
];

const ADVOCATE_ROLES = new Set(["managing_partner", "senior_advocate", "junior_advocate", "associate_advocate"]);

const STATES = ["Andhra Pradesh","Bihar","Delhi","Goa","Gujarat","Haryana","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Staff() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", password: "", role: "junior_advocate", phone: "",
        bar_council_number: "", enrollment_date: "", state: "", practice_areas: "",
    });
    const [saving, setSaving] = useState(false);
    const tableCtrl = useTableControls(items, {
        storageKey: "staff",
        initialSort: { key: "name", dir: "asc" },
        accessors: {
            status: r => (r.is_active === false ? "suspended" : "active"),
        },
    });

    const load = () => api.get("/users").then(r => setItems(r.data.filter(u => u.role !== "client_portal"))).catch(e => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            const payload = { ...form };
            payload.practice_areas = (form.practice_areas || "").split(",").map(s => s.trim()).filter(Boolean);
            if (!ADVOCATE_ROLES.has(form.role)) {
                delete payload.bar_council_number;
                delete payload.enrollment_date;
                delete payload.state;
                delete payload.practice_areas;
            }
            await api.post("/staff", payload);
            toast.success("Staff added", { description: `${form.email} · ${form.password}` });
            setOpen(false);
            setForm({ name: "", email: "", password: "", role: "junior_advocate", phone: "", bar_council_number: "", enrollment_date: "", state: "", practice_areas: "" });
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : apiError(e));
        } finally { setSaving(false); }
    };

    const toggle = async (u) => {
        try { await api.patch(`/staff/${u.id}`, { is_active: !u.is_active }); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Deactivate this staff member?")) return;
        try { await api.delete(`/staff/${id}`); load(); toast.success("Staff deactivated"); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Staff"
                subtitle="Onboard partners, advocates, clerks and accountants. Assign tasks and they're notified instantly."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="staff-add-btn"><UserPlus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Add staff</GoldButton>}
            />
            {items.length === 0 ? (
                <EmptyState title="No staff yet" subtitle="Build your firm's directory." action={<GoldButton onClick={() => setOpen(true)} data-testid="staff-empty-add">Add first member</GoldButton>}/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[640px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="name" ctrl={tableCtrl}>Name</SortableTh>
                                <SortableTh field="email" ctrl={tableCtrl}>Email</SortableTh>
                                <SortableTh field="role" ctrl={tableCtrl}>Role</SortableTh>
                                <SortableTh field="status" ctrl={tableCtrl}>Status</SortableTh>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(u => (
                                <tr key={u.id} className="border-b border-border hover:bg-muted/50" data-testid={`staff-row-${u.id}`}>
                                    <td className="px-4 py-3">{u.name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                                    <td className="px-4 py-3"><span className="overline">{u.role.replace(/_/g, " ")}</span></td>
                                    <td className="px-4 py-3"><StatusBadge status={u.is_active === false ? "suspended" : "active"}/></td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => toggle(u)} data-testid={`staff-toggle-${u.id}`} className="text-xs text-primary hover:underline mr-3">{u.is_active === false ? "Reactivate" : "Suspend"}</button>
                                        <button onClick={() => remove(u.id)} data-testid={`staff-delete-${u.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4 inline" strokeWidth={1.5}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="Add staff member" testId="staff-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Name"><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input"/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Email"><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input"/></Field>
                        <Field label="Mobile">
                            <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="staff-phone"/>
                        </Field>
                        <Field label="Initial password"><Input required minLength="6" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input"/></Field>
                        <Field label="Role"><Select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} options={ROLES} data-testid="staff-role-select"/></Field>
                    </div>
                    {ADVOCATE_ROLES.has(form.role) && (
                        <div className="bg-background border border-border p-4" data-testid="advocate-enrolment-section">
                            <div className="overline mb-3">Bar Council enrolment <span className="text-red-400 ml-1">required</span></div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Bar Council enrolment #">
                                    <Input required value={form.bar_council_number} onChange={e => setForm({ ...form, bar_council_number: e.target.value })} placeholder="e.g. MAH/1234/2018" data-testid="staff-bar-number-input"/>
                                </Field>
                                <Field label="Enrolment date">
                                    <Input required type="date" value={form.enrollment_date} onChange={e => setForm({ ...form, enrollment_date: e.target.value })} data-testid="staff-enrolment-date-input"/>
                                </Field>
                                <Field label="State">
                                    <Select required value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                                        options={[{value:"", label:"— select —"}, ...STATES.map(s => ({value: s, label: s}))]} data-testid="staff-state-select"/>
                                </Field>
                                <Field label="Practice areas (comma-separated)">
                                    <Input value={form.practice_areas} onChange={e => setForm({ ...form, practice_areas: e.target.value })} placeholder="Civil, Criminal, Family" data-testid="staff-practice-areas-input"/>
                                </Field>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving} data-testid="staff-save-btn">{saving ? "Adding…" : "Add staff"}</GoldButton></div>
                </form>
            </Modal>
        </>
    );
}
